import { ClerkProvider, RedirectToSignIn, SignedIn, SignedOut } from '@clerk/clerk-react'
import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6'
import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { Toaster } from '@/frontend/components/ui/sonner'
import { TooltipProvider } from '@/frontend/components/ui/tooltip'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { BreadcrumbProvider } from '@/frontend/hooks/use-breadcrumbs'

import { AppLayout } from './components/layout/app-layout'
import { ReloadOnNewRelease, ReloadOnNewReleaseFallback } from './components/reload-on-new-release'
import { queryClient } from './lib/query-client'
import { CatalogProductsAdminPage } from './pages/admin/catalog-products-page'
import { ClinicsAdminPage } from './pages/admin/clinics-page'
import { StaffAdminPage } from './pages/admin/staff-page'
import { AppointmentsPage } from './pages/appointments-page'
import { CheckoutPage } from './pages/checkout-page'
import { InventoryPage } from './pages/inventory-page'
import { MemberPage } from './pages/member-page'
import { MembersPage } from './pages/members-page'
import { NotFoundPage } from './pages/not-found-page'
import { PatientPage } from './pages/patient-page'
import { PatientsPage } from './pages/patients-page'
import { ProductStockPage } from './pages/product-stock-page'
import { RemoteConsultationsPage } from './pages/remote-consultations-page'
import { RemoteDispensingPage } from './pages/remote-dispensing-page'
import { SchedulePage } from './pages/schedule-page'
import { TasksPage } from './pages/tasks-page'
import { routes } from './routes'

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

function SentryUserSync() {
  const { data: staff } = useGetAuthenticatedStaff()
  useEffect(() => {
    Sentry.setUser(staff ?? null)
    return () => Sentry.setUser(null)
  }, [staff])
  return null
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ReloadOnNewReleaseFallback />}>
      <QueryClientProvider client={queryClient}>
        <ClerkProvider
          publishableKey={CLERK_PUBLISHABLE_KEY}
          appearance={{
            elements: {
              modalBackdrop: { zIndex: 50 },
              modalContent: { zIndex: 50 },
            },
          }}
        >
          <TooltipProvider>
            <BrowserRouter>
              <ReloadOnNewRelease />
              <NuqsAdapter>
                <BreadcrumbProvider>
                  <Routes>
                    <Route
                      path="/*"
                      element={
                        <>
                          <SignedIn>
                            <SentryUserSync />
                            <Routes>
                              <Route element={<AppLayout />}>
                                <Route index element={<Navigate to={routes.tasks} replace />} />
                                <Route path={routes.tasks} element={<TasksPage />} />
                                <Route
                                  path={routes.remoteConsultations}
                                  element={<RemoteConsultationsPage />}
                                />
                                <Route path={routes.members} element={<MembersPage />} />
                                <Route path={routes.member} element={<MemberPage />} />
                                <Route path={routes.patients} element={<PatientsPage />} />
                                <Route path={routes.patient} element={<PatientPage />} />
                                <Route path={routes.appointments} element={<AppointmentsPage />} />
                                <Route path={routes.schedule} element={<SchedulePage />} />

                                <Route path={routes.admin.clinics} element={<ClinicsAdminPage />} />
                                <Route path={routes.admin.staff} element={<StaffAdminPage />} />
                                <Route
                                  path={routes.admin.catalogProducts}
                                  element={<CatalogProductsAdminPage />}
                                />
                                <Route path={routes.checkout} element={<CheckoutPage />} />
                                <Route
                                  path={routes.dispensing}
                                  element={<RemoteDispensingPage />}
                                />
                                <Route path={routes.inventory} element={<InventoryPage />} />
                                <Route path={routes.productStock} element={<ProductStockPage />} />
                                <Route path="*" element={<NotFoundPage />} />
                              </Route>
                            </Routes>
                          </SignedIn>
                          <SignedOut>
                            <RedirectToSignIn />
                          </SignedOut>
                        </>
                      }
                    />
                  </Routes>
                </BreadcrumbProvider>
              </NuqsAdapter>
            </BrowserRouter>
          </TooltipProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </ClerkProvider>
        <Toaster position="top-center" />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  )
}
